import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, CalendarDays, Waves, ShieldCheck, LockKeyhole } from "lucide-react";
import { useContentSection } from "@/lib/siteData";

/**
 * Página original do Jet Paradise baseada nas SEÇÕES usuais de uma política
 * de cancelamento de passeios, com texto próprio e marca própria.
 * Os percentuais sugeridos não são política definitiva até aprovação.
 */
function CancellationPage({ sec }) {
    const groups = [];
    for (const line of (sec.body || "").split("\n").map((s) => s.trim()).filter(Boolean)) {
        if (line.startsWith("## ")) {
            groups.push({ heading: line.slice(3), rows: [] });
        } else if (groups.length) {
            groups[groups.length - 1].rows.push(line);
        }
    }

    return (
        <main
            className="grain min-h-screen bg-ocean px-4 pb-40 pt-8 font-body text-ink sm:px-7 sm:pb-28 sm:pt-12"
            data-testid="policy-cancelamento"
        >
            <div className="mx-auto max-w-3xl">
                <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-neon-cyan/30 bg-deep/70 px-4 text-xs font-bold text-neon-cyan transition hover:border-neon-cyan">
                    <ArrowLeft size={16} aria-hidden="true" /> Voltar para o site
                </Link>
                <div className="mt-11 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan">
                    <Waves size={20} aria-hidden="true" /> JET PARADISE · UBATUBA
                </div>
                <h1 className="mt-5 max-w-2xl font-display text-[clamp(27px,7vw,48px)] font-black uppercase leading-tight">
                    {sec.title}
                </h1>
                {sec.subtitle && <p className="mt-5 max-w-2xl text-base leading-8 text-dim">{sec.subtitle}</p>}

                <div className="mt-7 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4 text-sm leading-7 text-amber-100" role="note">
                    <div className="mb-1 flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-wider">
                        <AlertCircle size={18} aria-hidden="true" /> Minuta para aprovação
                    </div>
                    Esta é uma proposta de política para o Jet Paradise. Os prazos e percentuais abaixo ainda precisam de aprovação da empresa e revisão jurídica antes de se tornarem regras oficiais.
                </div>

                <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-neon-cyan">
                    {["Cancelamento", "Condições do mar", "Reembolso"].map((label) => (
                        <span key={label} className="rounded-full border border-neon-cyan/20 bg-deep/60 px-3 py-2">{label}</span>
                    ))}
                </div>

                <div className="mt-10 space-y-5">
                    {groups.map((group, index) => (
                        <section key={index} className="rounded-[26px] border border-neon-cyan/20 bg-[#11192a] p-5 shadow-[0_10px_35px_rgba(0,0,0,.12)] sm:p-8">
                            <h2 className="flex items-start gap-3 font-display text-[clamp(16px,4.2vw,22px)] font-extrabold leading-snug text-ink">
                                <span aria-hidden="true" className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neon-cyan/40 bg-neon-cyan/10 text-sm font-black text-neon-cyan">{index + 1}</span>
                                <span>{group.heading}</span>
                            </h2>
                            <div className="mt-5 space-y-3 pl-0 text-sm leading-7 text-dim sm:pl-12 sm:text-base">
                                {group.rows.map((row, i) => row.startsWith("### ") ? (
                                    <h3 key={i} className="pt-3 font-display text-sm font-bold text-neon-cyan sm:text-base">{row.slice(4)}</h3>
                                ) : (
                                    <p key={i}>{row}</p>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-8 rounded-2xl border border-neon-cyan/30 bg-neon-cyan/10 p-5 sm:p-7">
                    <div className="flex items-center gap-2 font-display text-sm font-bold text-neon-cyan">
                        <CalendarDays size={20} aria-hidden="true" /> Precisa alterar uma reserva?
                    </div>
                    <p className="mt-3 text-sm leading-7 text-dim">
                        Fale com a equipe pelos canais oficiais do Jet Paradise e informe a data do passeio e o código da reserva, se houver. No celular, o WhatsApp está na barra inferior da tela.
                    </p>
                </div>
                <p className="mt-7 text-center text-xs leading-6 text-dim/80">
                    Esta minuta não elimina os direitos assegurados pela legislação brasileira de proteção ao consumidor.
                </p>
            </div>
        </main>
    );
}

function PrivacyPage({ sec }) {
    const groups = [];
    for (const line of (sec.body || "").split("\n").map(s => s.trim()).filter(Boolean)) {
        if (line.startsWith("## ")) groups.push({ title: line.slice(3), lines: [] });
        else if (groups.length) groups[groups.length - 1].lines.push(line);
    }
    return (
        <main className="grain min-h-screen bg-ocean px-4 pb-40 pt-8 font-body text-ink sm:px-7 sm:pb-28 sm:pt-12" data-testid="policy-privacidade">
            <div className="mx-auto max-w-3xl">
                <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-neon-cyan/30 bg-deep/70 px-4 text-xs font-bold text-neon-cyan transition hover:border-neon-cyan">
                    <ArrowLeft size={16} aria-hidden="true" /> Voltar para o site
                </Link>
                <div className="mt-11 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan">
                    <ShieldCheck size={20} aria-hidden="true" /> JET PARADISE · UBATUBA
                </div>
                <h1 className="mt-5 font-display text-[clamp(27px,7vw,48px)] font-black uppercase leading-tight">
                    {sec.title}
                </h1>
                {sec.subtitle && <p className="mt-5 max-w-2xl text-base leading-8 text-dim">{sec.subtitle}</p>}
                <div className="mt-7 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4 text-sm leading-7 text-amber-100" role="note">
                    <div className="mb-1 flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-wider">
                        <AlertCircle size={18} aria-hidden="true" /> Versão para revisão
                    </div>
                    Os dados de identificação da empresa, o contato de privacidade, os prazos de armazenamento e os prestadores efetivamente utilizados precisam ser confirmados antes de adotar esta política como definitiva.
                </div>
                <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-neon-cyan">
                    {["Dados pessoais", "LGPD", "Reservas", "Seus direitos"].map(label =>
                        <span key={label} className="rounded-full border border-neon-cyan/20 bg-deep/60 px-3 py-2">{label}</span>
                    )}
                </div>
                <div className="mt-10 space-y-5">
                    {groups.map((group,index) =>
                        <section key={index} className="rounded-[26px] border border-neon-cyan/20 bg-[#11192a] p-5 shadow-[0_10px_35px_rgba(0,0,0,.12)] sm:p-8">
                            <h2 className="flex items-start gap-3 font-display text-[clamp(16px,4.2vw,22px)] font-extrabold leading-snug text-ink">
                                <span aria-hidden="true" className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neon-cyan/40 bg-neon-cyan/10 text-sm font-black text-neon-cyan">{index + 1}</span>
                                <span>{group.title}</span>
                            </h2>
                            <div className="mt-5 space-y-3 text-sm leading-7 text-dim sm:pl-12 sm:text-base">
                                {group.lines.map((line,i) => line.startsWith("### ") ?
                                    <h3 key={i} className="pt-3 font-display text-sm font-bold text-neon-cyan sm:text-base">{line.slice(4)}</h3> :
                                    <p key={i}>{line}</p>)}
                            </div>
                        </section>
                    )}
                </div>
                <div className="mt-8 rounded-2xl border border-neon-cyan/30 bg-neon-cyan/10 p-5 sm:p-7">
                    <div className="flex items-center gap-2 font-display text-sm font-bold text-neon-cyan">
                        <LockKeyhole size={20} aria-hidden="true"/> Dúvidas sobre seus dados?
                    </div>
                    <p className="mt-3 text-sm leading-7 text-dim">
                        Utilize os canais oficiais de contato publicados no Jet Paradise e identifique a solicitação como “Privacidade e proteção de dados”. O contato específico da pessoa responsável será informado após validação da empresa.
                    </p>
                </div>
            </div>
        </main>
    );
}

export default function PolicyPage({ sectionKey }) {
    const sec = useContentSection(sectionKey);
    useEffect(() => {
        const previous = document.title;
        document.title = `${sec.title} | Jetski Paradise`;
        return () => { document.title = previous; };
    }, [sec.title]);

    if (sectionKey === "cancelamento") {
        return <CancellationPage sec={sec} />;
    }
    if (sectionKey === "privacidade") {
        return <PrivacyPage sec={sec} />;
    }

    return (
        <main className="grain min-h-screen bg-ocean px-5 pb-28 pt-12 font-body text-ink" data-testid={`policy-${sectionKey}`}>
            <article className="mx-auto max-w-3xl">
                <Link to="/" className="font-mono2 text-xs uppercase tracking-[0.2em] text-neon-cyan">← Voltar ao site</Link>
                <h1 className="mt-12 font-display text-3xl font-black uppercase sm:text-5xl">{sec.title}</h1>
                {sec.subtitle && <p className="mt-4 text-dim">{sec.subtitle}</p>}
                <div className="mt-10 space-y-4 leading-relaxed text-dim">
                    {(sec.body || "").split("\n").filter((l) => l.trim()).map((line, i) =>
                        line.startsWith("## ")
                            ? <h2 key={i} className="pt-6 font-display text-lg font-bold text-ink">{line.slice(3)}</h2>
                            : <p key={i}>{line}</p>)}
                </div>
            </article>
        </main>
    );
}
